// Quote Builder — Session defaults strip (board finish — supplier-agnostic
// port of kos-qb-v3.js:10645–10890 `renderDtnSession` + handlers).
//
// SOT is DTN-only; this modular version is supplier-parameterised. The
// strip mounts above the chip strip on the left-pane Products tab whenever
// the Cabinets chip is active. LS keys are per-supplier so each supplier
// remembers its own session default independently. The mutate-on-selected-
// cabinet side effect (SOT lines 10885–10888) is preserved via a pubsub
// the cabinet-picker subscribes to.
//
// Edging colour follows in a separate module addition once this is signed
// off — do not extend this file with edging fields yet.
//
// Finishes source per supplier:
//   • PickerSupplier.hasFinishesEndpoint=true → GET /api/{slug}/finishes
//   • Otherwise → DEFAULT_CARCASS_FINISHES (12-finish fallback)

import { api } from "@tkos/api-client";
import type { PickerSupplier, SessionFinish } from "./types";
import { DEFAULT_CARCASS_FINISHES, PICKER_SUPPLIERS } from "./types";
import { escapeHtml } from "./formatters";
import type { QuoteStore } from "./state";

// ── LS key scoping ──────────────────────────────────────────────────────

// Per-supplier short alias for LS keys. Matches the tab-key convention
// already in use in products-panel.ts (TKC / DTN / LDL / CUSTOM) and keeps
// keys grep-friendly. SOT uses `kos_dtn_session_finish` — we generalise
// to `kos_session_finish_{ALIAS}` so each supplier persists separately.
function shortSupplierAlias(supplierKey: string): string {
  switch (supplierKey) {
    case "TKC":                   return "TKC";
    case "Trade Kitchens by DTN": return "DTN";
    case "LDL":                   return "LDL";
    case "PWS":                   return "PWS";
    case "Omega":                 return "OMEGA";
    case "Manual":                return "MANUAL";
    default:                      return supplierKey.replace(/[^A-Za-z0-9]+/g, "_").toUpperCase();
  }
}

function lsKey(supplierKey: string): string {
  return `kos_session_finish_${shortSupplierAlias(supplierKey)}`;
}

// ── In-memory state ─────────────────────────────────────────────────────

interface FinishCache {
  list: SessionFinish[] | null;
  loading: boolean;
  callbacks: Array<(list: SessionFinish[]) => void>;
}

// Keyed by PickerSupplier.key.
const FINISHES_CACHE = new Map<string, FinishCache>();

function cacheFor(supplierKey: string): FinishCache {
  let c = FINISHES_CACHE.get(supplierKey);
  if (!c) {
    c = { list: null, loading: false, callbacks: [] };
    FINISHES_CACHE.set(supplierKey, c);
  }
  return c;
}

// Cached per-supplier session-finish reads (LS-backed). Loaded lazily.
const SESSION_FINISHES = new Map<string, SessionFinish | null>();

function loadSessionFinishFromLS(supplierKey: string): SessionFinish | null {
  if (SESSION_FINISHES.has(supplierKey)) return SESSION_FINISHES.get(supplierKey) ?? null;
  let value: SessionFinish | null = null;
  try {
    const raw = localStorage.getItem(lsKey(supplierKey));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<SessionFinish>;
      if (parsed && typeof parsed.code === "string" && typeof parsed.pg === "number") {
        value = {
          code: parsed.code,
          name: typeof parsed.name === "string" ? parsed.name : parsed.code,
          label: typeof parsed.label === "string" ? parsed.label : (parsed.name ?? parsed.code),
          pg: (parsed.pg as 1 | 2 | 3 | 4 | 5),
        };
      }
    }
  } catch { /* swallow corrupt LS */ }
  SESSION_FINISHES.set(supplierKey, value);
  return value;
}

function persistSessionFinish(supplierKey: string, finish: SessionFinish | null): void {
  SESSION_FINISHES.set(supplierKey, finish);
  try {
    if (finish) localStorage.setItem(lsKey(supplierKey), JSON.stringify(finish));
    else localStorage.removeItem(lsKey(supplierKey));
  } catch { /* ignore quota */ }
}

// ── Pubsub ──────────────────────────────────────────────────────────────

type Listener = (supplierKey: string, finish: SessionFinish | null) => void;
const LISTENERS = new Set<Listener>();

export function subscribeSessionFinish(fn: Listener): () => void {
  LISTENERS.add(fn);
  return () => { LISTENERS.delete(fn); };
}

function emit(supplierKey: string, finish: SessionFinish | null): void {
  for (const fn of LISTENERS) {
    try { fn(supplierKey, finish); } catch { /* swallow */ }
  }
}

// ── Public reads / writes ───────────────────────────────────────────────

export function getSessionFinish(supplierKey: string): SessionFinish | null {
  return loadSessionFinishFromLS(supplierKey);
}

export function setSessionFinish(supplierKey: string, finish: SessionFinish | null): void {
  persistSessionFinish(supplierKey, finish);
  emit(supplierKey, finish);
}

// ── Finishes loader (supplier-parameterised) ────────────────────────────

interface DtnFinishRow {
  brand?: string;
  name?: string;
  code?: string;
  structure?: string;
  pg?: number;
  label?: string;
}

function projectDtnRow(r: DtnFinishRow): SessionFinish | null {
  const code = String(r.code ?? "").trim();
  const name = String(r.name ?? "").trim();
  if (!code || !name) return null;
  const pgRaw = Number(r.pg ?? 0);
  const pg = (pgRaw >= 1 && pgRaw <= 5 ? pgRaw : 1) as 1 | 2 | 3 | 4 | 5;
  const parts: string[] = [];
  if (r.brand) parts.push(String(r.brand));
  parts.push(name);
  if (r.structure) parts.push(String(r.structure));
  const label = r.label ? String(r.label) : parts.join(" · ");
  return { code, name, label, pg };
}

function defaultFinishesAsSession(): SessionFinish[] {
  return DEFAULT_CARCASS_FINISHES.map((f) => ({
    code: f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
    name: f.name,
    label: f.name,
    pg: f.priceGroup,
  }));
}

export async function loadFinishesFor(supplierKey: string): Promise<SessionFinish[]> {
  const cache = cacheFor(supplierKey);
  if (cache.list) return cache.list;
  if (cache.loading) {
    return new Promise((resolve) => { cache.callbacks.push(resolve); });
  }
  cache.loading = true;
  const supplier: PickerSupplier | undefined = PICKER_SUPPLIERS.find((s) => s.key === supplierKey);

  let list: SessionFinish[];
  if (supplier?.hasFinishesEndpoint && supplier.finishesSlug) {
    try {
      const data = await api<{ results?: DtnFinishRow[] }>(`/${supplier.finishesSlug}/finishes?limit=1000`);
      const rows = Array.isArray(data.results) ? data.results : [];
      const projected = rows.map(projectDtnRow).filter((x): x is SessionFinish => x !== null);
      list = projected.length > 0 ? projected : defaultFinishesAsSession();
    } catch {
      list = defaultFinishesAsSession();
    }
  } else {
    list = defaultFinishesAsSession();
  }

  cache.list = list;
  cache.loading = false;
  for (const cb of cache.callbacks) cb(list);
  cache.callbacks = [];
  return list;
}

// ── Mount API ───────────────────────────────────────────────────────────

export interface SessionDefaultsHandle {
  unmount(): void;
}

/** Mount the session-defaults strip into `host`. The host is the empty
 *  container above the chip strip in the left-pane Products panel. Caller
 *  is responsible for mounting only when the Cabinets chip is active (the
 *  strip is cabinet-specific, per Steve 2026-05-15). */
export function mountSessionDefaults(
  host: HTMLElement,
  _store: QuoteStore,
  supplierKey: string,
): SessionDefaultsHandle {
  let mounted = true;

  function paint(list: SessionFinish[] | null, current: SessionFinish | null): void {
    if (!mounted) return;
    const supplier = PICKER_SUPPLIERS.find((s) => s.key === supplierKey);
    const sourceTag = supplier?.hasFinishesEndpoint ? "live" : "default set";
    if (!list) {
      host.innerHTML = `
        <div class="qb-session" data-role="qb-session">
          <label class="qb-session-lbl">Board Finish (session default)</label>
          <div class="qb-session-loading">Loading ${escapeHtml(supplierKey)} finishes…</div>
        </div>
      `;
      return;
    }
    const opts = list.map((f) => {
      const selected = current && current.code === f.code ? " selected" : "";
      return `<option value="${escapeHtml(f.code)}" data-pg="${f.pg}"${selected}>${escapeHtml(f.label)} · PG${f.pg}</option>`;
    }).join("");
    const badge = current
      ? `<span class="qb-session-badge">PG${current.pg} · ${escapeHtml(current.name)}</span>`
      : "";
    host.innerHTML = `
      <div class="qb-session" data-role="qb-session" data-supplier="${escapeHtml(supplierKey)}">
        <label class="qb-session-lbl">Board Finish (session default) · ${escapeHtml(supplierKey)} <span class="qb-session-source">(${escapeHtml(sourceTag)})</span></label>
        <select class="qb-session-select" data-role="qb-session-select">
          <option value="">— Select Board Finish —</option>
          ${opts}
        </select>
        ${badge}
      </div>
    `;
    wire(list);
  }

  function wire(list: SessionFinish[]): void {
    const select = host.querySelector<HTMLSelectElement>("[data-role='qb-session-select']");
    if (!select) return;
    select.addEventListener("change", (e) => {
      const value = (e.target as HTMLSelectElement).value;
      if (!value) {
        setSessionFinish(supplierKey, null);
        paint(list, null);
        return;
      }
      const picked = list.find((f) => f.code === value) ?? null;
      setSessionFinish(supplierKey, picked);
      paint(list, picked);
    });
  }

  // Initial paint — loading state, then the real list.
  paint(null, loadSessionFinishFromLS(supplierKey));
  void loadFinishesFor(supplierKey).then((list) => {
    if (!mounted) return;
    paint(list, loadSessionFinishFromLS(supplierKey));
  });

  return {
    unmount(): void {
      mounted = false;
      host.innerHTML = "";
    },
  };
}
